import * as React from "react";

import { usePrefersReducedMotion } from "@/features/landing/usePrefersReducedMotion";

/**
 * Typewriter text (the React Bits "Text Type" element), hand-rolled rather than pulled in as a
 * dependency — the same call the repo already makes for card-swap, scroll-float and the masonry
 * (design principle 18, "keep it dependency-light").
 *
 * <p>Types each string out character by character, optionally deletes it and moves to the next.
 * With a single string and {@code loop} off it simply types once and stops, which is what a
 * headline wants.
 *
 * <p>Accessibility: the animated characters are {@code aria-hidden} and the full text is exposed
 * once via {@code aria-label}, so assistive tech reads the finished sentence rather than a
 * half-typed one. Under {@code prefers-reduced-motion} the whole effect is skipped and the final
 * text renders immediately, cursor included but static.
 */
export function TextType({
  text,
  as: Tag = "span",
  typingSpeed = 55,
  deletingSpeed = 28,
  initialDelay = 250,
  pauseDuration = 2200,
  loop = false,
  showCursor = true,
  cursorCharacter = "|",
  className,
  cursorClassName,
}: {
  text: string | string[];
  /**
   * Element to render as. The headline passes "h1". Deliberately a short union rather than
   * `keyof JSX.IntrinsicElements`: react-three-fiber augments that namespace globally, so the
   * open version resolves to a union including drei's <Image>, which demands a `map` prop.
   */
  as?: "h1" | "h2" | "h3" | "p" | "div" | "span";
  typingSpeed?: number;
  deletingSpeed?: number;
  initialDelay?: number;
  /** How long a completed sentence sits before it starts deleting. */
  pauseDuration?: number;
  loop?: boolean;
  showCursor?: boolean;
  cursorCharacter?: string;
  className?: string;
  cursorClassName?: string;
}) {
  const sentences = React.useMemo(() => (Array.isArray(text) ? text : [text]), [text]);
  const reduced = usePrefersReducedMotion();

  const [index, setIndex] = React.useState(0);
  const [shown, setShown] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [started, setStarted] = React.useState(false);

  const full = sentences[index] ?? "";
  // The last sentence of a non-looping run is the resting state: stop, keep it on screen.
  const finished = !loop && index === sentences.length - 1 && shown === full;

  React.useEffect(() => {
    if (reduced) return;
    const id = window.setTimeout(() => setStarted(true), initialDelay);
    return () => window.clearTimeout(id);
  }, [reduced, initialDelay]);

  React.useEffect(() => {
    if (reduced || !started || finished) return;

    if (!deleting && shown === full) {
      if (sentences.length === 1 && !loop) return;
      const id = window.setTimeout(() => setDeleting(true), pauseDuration);
      return () => window.clearTimeout(id);
    }

    if (deleting && shown === "") {
      setDeleting(false);
      setIndex((i) => (i + 1) % sentences.length);
      return;
    }

    const id = window.setTimeout(
      () => setShown((s) => (deleting ? full.slice(0, s.length - 1) : full.slice(0, s.length + 1))),
      deleting ? deletingSpeed : typingSpeed,
    );
    return () => window.clearTimeout(id);
  }, [
    reduced,
    started,
    finished,
    deleting,
    shown,
    full,
    sentences.length,
    loop,
    pauseDuration,
    typingSpeed,
    deletingSpeed,
  ]);

  const label = sentences.join(" ");
  const body = reduced ? sentences[sentences.length - 1] : shown;

  return (
    <Tag className={className} aria-label={label}>
      <span aria-hidden="true">{body}</span>
      {showCursor && (
        <span
          aria-hidden="true"
          className={cursorClassName}
          data-static={reduced || finished ? "" : undefined}
        >
          {cursorCharacter}
        </span>
      )}
    </Tag>
  );
}
